// src/pages/approval/Mydocs.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  X,
  RotateCcw,
} from "lucide-react";
import axios from "../../api/axios";

// 상태 스타일
const statusStyle = (s) => {
  const styles = {
    approved: "bg-emerald-50 text-emerald-700",
    rejected: "bg-red-50 text-red-700",
    cancel: "bg-slate-100 text-slate-600",
    in_progress: "bg-blue-50 text-blue-700",
    submit: "bg-amber-50 text-amber-700",
  };
  return styles[s] || "bg-slate-100 text-slate-600";
};

const statusText = (s) =>
  ({
    submit: "상신",
    in_progress: "진행중",
    approved: "승인",
    rejected: "반려",
    cancel: "취소",
  }[s] ||
  s ||
  "-");

const moduleText = (s) =>
  ({
    approval: "결재",
    material: "자재",
    safety: "안전",
    quality: "품질",
    design: "설계",
    construction: "시공",
    const: "시공",
  }[s] ||
  s ||
  "-");

// 시공(const) 모듈의 doc_type을 실제 라우트 경로로 매핑
const getConstRoutePath = (docType) => {
  const typeMap = {
    DAILY: "daily",
    DA: "daily",
    WO: "order",
    WORKORDER: "order",
    OR: "ordercomplete",
    ORDERCOMPLETE: "ordercomplete",
    PROGRESS: "progress",
    PR: "progress",
    PHOTO: "photo",
    PH: "photo",
    PHOTOBOARD: "photoboard",
    PB: "photoboard",
  };
  return (
    typeMap[(docType || "").toUpperCase()] || docType?.toLowerCase() || "daily"
  );
};

// 모듈과 doc_type에 따른 실제 뷰 경로 생성
const buildViewPath = (projectId, module, docType, dns) => {
  let m = module || "approval";
  if (m === "construction") m = "const";

  // 시공 모듈은 doc_type을 라우트 경로로 변환
  if (m === "const") {
    const routePath = getConstRoutePath(docType);
    return `/project/${projectId}/const/${routePath}/${dns}/view`;
  }

  // 자재 모듈: material → materials (복수형)
  if (m === "material") {
    const type = (docType || "ps").toLowerCase();
    return `/project/${projectId}/materials/${type}/${dns}/view`;
  }

  // 설계 모듈: doc_type 소문자 변환 (FCR → fcr)
  if (m === "design") {
    const type = (docType || "fcr").toLowerCase();
    return `/project/${projectId}/design/${type}/${dns}/view`;
  }

  // 다른 모듈은 기존대로 (quality, safety, approval 등)
  return `/project/${projectId}/${m}/${docType || "APP"}/${dns}/view`;
};

const STATUS_OPTIONS = [
  { value: "all", label: "전체 상태" },
  { value: "submit", label: "상신" },
  { value: "in_progress", label: "진행중" },
  { value: "approved", label: "승인" },
  { value: "rejected", label: "반려" },
  { value: "cancel", label: "취소" },
];

const MODULE_OPTIONS = [
  { value: "all", label: "전체 모듈" },
  { value: "approval", label: "결재" },
  { value: "quality", label: "품질" },
  { value: "safety", label: "안전" },
  { value: "design", label: "설계" },
  { value: "const", label: "시공" },
  { value: "material", label: "자재" },
];

export default function Mydocs() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // 검색 관련
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("title"); // title, author, dns, all
  const [filterType, setFilterType] = useState("all"); // all, to_approve, my_docs
  const [statusFilter, setStatusFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");

  useEffect(() => {
    let aborted = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get("approval/mylist/", {
          params: {
            project: projectId,
            page,
            page_size: pageSize,
            filter_type: filterType,
          },
        });
        if (aborted) return;
        const list = Array.isArray(res.data?.results)
          ? res.data.results
          : Array.isArray(res.data)
          ? res.data
          : [];
        setRows(list);
        setTotal(Number(res.data?.count || list.length || 0));
      } catch (e) {
        if (!aborted) setRows([]);
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => {
      aborted = true;
    };
  }, [page, pageSize, filterType]);

  const filtered = useMemo(() => {
    let result = rows;

    // 상태 필터
    if (statusFilter !== "all") {
      result = result.filter((doc) => doc.status === statusFilter);
    }

    // 모듈 필터
    if (moduleFilter !== "all") {
      result = result.filter((doc) => {
        let module = doc.module || "approval";
        if (module === "construction") module = "const";
        return module === moduleFilter;
      });
    }

    // 검색어 필터
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter((doc) => {
        const title = String(doc.title || "").toLowerCase();
        const authorName = String(doc?.created_by?.name || "").toLowerCase();
        const dns = String(doc.dns || doc.id || "").toLowerCase();

        switch (searchField) {
          case "title":
            return title.includes(q);
          case "author":
            return authorName.includes(q);
          case "dns":
            return dns.includes(q);
          default:
            return (
              title.includes(q) || authorName.includes(q) || dns.includes(q)
            );
        }
      });
    }

    return result;
  }, [rows, searchTerm, searchField, statusFilter, moduleFilter]);

  const totalPages = Math.ceil(total / pageSize) || 1;

  const handleReset = () => {
    setSearchTerm("");
    setSearchField("title");
    setStatusFilter("all");
    setModuleFilter("all");
    setPage(1);
  };

  // 필터가 활성화되었는지 확인
  const hasActiveFilters =
    searchTerm ||
    searchField !== "title" ||
    statusFilter !== "all" ||
    moduleFilter !== "all";

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="mx-auto max-w-lg px-4 pt-4 pb-28">
        {/* 헤더 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">전자결재</p>
          <h2 className="text-xl font-bold text-gray-900">내 결재 문서</h2>
          <p className="mt-1 text-sm text-gray-500">총 {total}건의 문서</p>
        </header>

        {/* 필터 탭 */}
        <section className="mb-4">
          <div className="flex gap-1 rounded-xl bg-gray-100 p-1">
            {[
              { key: "all", label: "전체" },
              { key: "to_approve", label: "결재할 문서" },
              { key: "my_docs", label: "작성한 문서" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setFilterType(tab.key);
                  setPage(1);
                }}
                className={`flex-1 rounded-lg py-2 text-sm font-medium transition-all ${
                  filterType === tab.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        {/* 검색 영역 */}
        <section className="mb-4 space-y-2">
          {/* 검색 입력창 */}
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <Search size={18} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="검색어를 입력하세요"
              className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* 필터 드롭박스들 */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* 검색 필드 */}
            <div className="relative">
              <select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                className="appearance-none rounded-full border border-gray-200 bg-white pl-3 pr-7 py-2 text-xs text-gray-700 outline-none shadow-sm focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
              >
                <option value="title">제목</option>
                <option value="author">작성자</option>
                <option value="dns">문서번호</option>
                <option value="all">전체 검색</option>
              </select>
              <ChevronDown
                size={12}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>

            {/* 상태 필터 */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`appearance-none rounded-full border pl-3 pr-7 py-2 text-xs outline-none shadow-sm transition-colors ${
                  statusFilter !== "all"
                    ? "border-blue-300 bg-blue-50 text-blue-700"
                    : "border-gray-200 bg-white text-gray-700"
                }`}
              >
                <option value="all">상태: 전체</option>
                <option value="submit">상태: 상신</option>
                <option value="in_progress">상태: 진행중</option>
                <option value="approved">상태: 승인</option>
                <option value="rejected">상태: 반려</option>
                <option value="cancel">상태: 취소</option>
              </select>
              <ChevronDown
                size={12}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>

            {/* 모듈 필터 */}
            <div className="relative">
              <select
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                className={`appearance-none rounded-full border pl-3 pr-7 py-2 text-xs outline-none shadow-sm transition-colors ${
                  moduleFilter !== "all"
                    ? "border-green-300 bg-green-50 text-green-700"
                    : "border-gray-200 bg-white text-gray-700"
                }`}
              >
                <option value="all">모듈: 전체</option>
                <option value="approval">모듈: 결재</option>
                <option value="quality">모듈: 품질</option>
                <option value="safety">모듈: 안전</option>
                <option value="design">모듈: 설계</option>
                <option value="const">모듈: 시공</option>
                <option value="material">모듈: 자재</option>
              </select>
              <ChevronDown
                size={12}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>

            {/* 초기화 버튼 */}
            <button
              onClick={handleReset}
              className={`flex items-center justify-center gap-1 rounded-full border px-3 py-2 text-xs font-medium shadow-sm transition-colors ${
                hasActiveFilters
                  ? "border-blue-300 bg-blue-50 text-blue-600"
                  : "border-gray-200 bg-white text-gray-500"
              }`}
            >
              <RotateCcw size={12} />
              초기화
            </button>
          </div>
        </section>

        {/* 목록 */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-sm text-gray-400">불러오는 중...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-sm text-gray-400">문서가 없습니다.</p>
          </div>
        ) : (
          <section className="space-y-2">
            {filtered.map((row) => {
              const docDns = row.dns || row.id || row.docs_dns;
              const module = row.module || "approval";
              const type = row.doc_type || "APP";

              return (
                <button
                  key={docDns}
                  onClick={() =>
                    navigate(buildViewPath(projectId, module, type, docDns))
                  }
                  className="w-full rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-1">
                          {row.title}
                        </p>
                        {row.is_urgent && (
                          <span className="flex items-center gap-0.5 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600">
                            <AlertCircle size={10} />
                            긴급
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                        <span>{row?.created_by?.name || "작성자 미상"}</span>
                        <span className="text-gray-300">·</span>
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium">
                          {moduleText(row.module)}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span>
                          {row.created_at
                            ? new Date(row.created_at).toLocaleDateString()
                            : "-"}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${statusStyle(
                        row.status
                      )}`}
                    >
                      {statusText(row.status)}
                    </span>
                  </div>
                </button>
              );
            })}
          </section>
        )}

        {/* 페이지네이션 */}
        {!loading && filtered.length > 0 && (
          <footer className="mt-6 flex items-center justify-center gap-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 shadow-sm disabled:opacity-40 active:scale-[0.95]"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-1 text-sm">
              <span className="font-semibold text-gray-900">{page}</span>
              <span className="text-gray-400">/</span>
              <span className="text-gray-500">{totalPages}</span>
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 shadow-sm disabled:opacity-40 active:scale-[0.95]"
            >
              <ChevronRight size={20} />
            </button>
          </footer>
        )}
      </main>

      {/* 하단 액션 버튼 - 전체 너비로 확장 */}
      <footer className="fixed inset-x-0 bottom-0 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-4 py-3 safe-area-bottom z-50">
        <div className="mx-auto max-w-lg">
          <button
            onClick={() =>
              navigate(`/project/${projectId}/approval/request/new`)
            }
            className="w-full h-12 rounded-full bg-blue-500 text-sm font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg hover:bg-blue-600"
          >
            <Plus size={18} />
            결재 상신
          </button>
        </div>
      </footer>
    </div>
  );
}
